/**
 * Unified Pricing Display Utility — Single Source of Truth
 * =========================================================
 * ALL pricing display across the site MUST use these functions.
 * Never render raw p.price strings directly.
 *
 * Concepts handled:
 *  - Recurring monthly pricing ("/mo")
 *  - One-time evaluation pricing
 *  - Activation fee (separate from recurring cost)
 *  - Discount calculation
 *  - Cadence normalization ("/30d" → "/mo")
 */

/**
 * Compute a discounted price from a base numeric value and discount percentage.
 * Single source of truth — replaces all inline `base * (1 - pct/100)` patterns.
 *
 * @param {number} baseNumeric - The base price as a positive number
 * @param {number} discountPercent - The discount percentage (e.g. 60 for 60% off)
 * @returns {number|null} The discounted price rounded to nearest integer, or null
 */
export const computeDiscountedPrice = (baseNumeric, discountPercent) => {
  if (typeof baseNumeric !== "number" || baseNumeric <= 0) return null;
  if (typeof discountPercent !== "number" || discountPercent <= 0) return null;
  return Math.round(baseNumeric * (1 - discountPercent / 100));
};

/**
 * Parse numeric dollar amount from a price string.
 * Handles: "$197/mo", "$149", "$59/30d + $99 activation", "$69"
 * Splits on "/" and "+" to avoid digit contamination.
 */
export const parsePrice = (priceStr) => {
  if (!priceStr) return 0;
  const s = String(priceStr);
  // Take part before "+" to strip activation addendums
  const beforePlus = s.split("+")[0];
  // Take part before "/" to strip cadence suffix
  const mainPart = beforePlus.split("/")[0];
  return parseFloat(mainPart.replace(/[^0-9.]/g, "")) || 0;
};

/**
 * Determine if pricing is recurring (monthly) or one-time.
 * Uses price_type field first, then falls back to price string analysis.
 */
const isRecurringPrice = (pricingItem) => {
  if (!pricingItem) return false;
  const pt = (pricingItem.price_type || "").toLowerCase();
  if (pt === "monthly" || pt === "subscription") return true;
  if (pt === "one-time") return false;
  // Fallback: check price string for cadence markers
  const p = String(pricingItem.price || "");
  return p.includes("/mo") || p.includes("/30d") || p.includes("/month");
};

/**
 * Get the normalized cadence suffix for display.
 * Always returns "/mo" for recurring, "" for one-time.
 */

/**
 * Format a pricing item into a clean display object.
 * This is the PRIMARY function all surfaces should use.
 *
 * @param {Object} pricingItem - { price, price_type, size, ... }
 * @param {Object} [firm] - Optional firm object for activation_fee fallback
 * @returns {Object} Structured pricing display data
 */
export const formatFirmPrice = (pricingItem, firm = null) => {
  if (!pricingItem) {
    return {
      amount: "Contact",
      raw: 0,
      cadence: "",
      cadenceLabel: "",
      isRecurring: false,
      label: "Price",
      display: "Contact",
      activationFee: null,
    };
  }

  const raw = parsePrice(pricingItem.price);
  const recurring = isRecurringPrice(pricingItem);
  const cadence = recurring ? "/mo" : "";
  const cadenceLabel = recurring ? "monthly" : "one-time";
  const label = "Price";
  const amount = raw > 0 ? `$${raw}` : "Contact";
  const display = raw > 0 ? `$${raw}${cadence}` : "Contact";

  // Activation fee: check pricing item first, then firm-level
  let activationFee = null;
  if (firm) {
    const af = (firm.activation_fee || "").toLowerCase();
    if (af && af !== "none" && af !== "$0" && af !== "0" && af !== "included" && af !== "n/a") {
      activationFee = firm.activation_fee;
    }
  }

  return { amount, raw, cadence, cadenceLabel, isRecurring: recurring, label, display, activationFee };
};


/**
 * Parse activation fee string and resolve per-size fee when possible.
 *
 * Patterns handled:
 *  A/B  Per-size:   "$98 (10K) / $143 (25K) / $148 (50K)"
 *  C    Flat fee:   "$149"
 *  D    Zero:       "$0"
 *  E    None:       "None", null, undefined, ""
 *  F    Included:   "Included"
 *  G    Complex:    "$47 one-time or $87/mo (one-time: $177-$247)"
 *
 * @param {string|null|undefined} rawString — activation_fee from firm object
 * @param {string} [sizeLabel] — e.g. "$50K", "100K" (same format as pricingItem.size)
 * @returns {{ feeForSize: string|null, isPerSize: boolean, rawString: string|null }}
 */
export const parseActivationFee = (rawString, sizeLabel) => {
  // Pattern E — null / empty / undefined
  if (!rawString || rawString.trim() === "") {
    return { feeForSize: null, isPerSize: false, rawString: null };
  }

  const trimmed = rawString.trim();
  const lower = trimmed.toLowerCase();

  // Pattern E — "None", "N/A"
  if (lower === "none" || lower === "n/a") {
    return { feeForSize: null, isPerSize: false, rawString: trimmed };
  }

  // Pattern F — "Included"
  if (lower === "included") {
    return { feeForSize: "Included", isPerSize: false, rawString: trimmed };
  }

  // Pattern D — "$0" or "0"
  if (lower === "$0" || lower === "0") {
    return { feeForSize: "$0", isPerSize: false, rawString: trimmed };
  }

  // Detect per-size parenthetical pattern: "$X (label)"
  const perSizePattern = /\$[\d,.]+\s*\([^)]+\)/;
  const hasPerSize = perSizePattern.test(trimmed);

  if (!hasPerSize) {
    // Pattern G — complex (contains " or " or range "$X-$Y")
    // Return feeForSize: null so the rawString fallback extracts properly
    if (lower.includes(" or ") || /\$[\d,.]+-\$[\d,.]+/.test(trimmed)) {
      return { feeForSize: null, isPerSize: false, rawString: trimmed };
    }

    // Pattern C — flat fee (e.g. "$149")
    if (/^\$[\d,.]+$/.test(trimmed)) {
      return { feeForSize: trimmed, isPerSize: false, rawString: trimmed };
    }

    // Unknown — safe fallback to raw string
    return { feeForSize: trimmed, isPerSize: false, rawString: trimmed };
  }

  // Pattern A/B — per-size with parenthetical segments
  const segments = trimmed.split(/\s*\/\s*/);
  const parsed = segments
    .map((seg) => {
      const match = seg.match(/(\$[\d,.]+)\s*\(([^)]+)\)/);
      if (!match) return null;
      return { fee: match[1], size: match[2].trim() };
    })
    .filter(Boolean);

  if (parsed.length === 0) {
    return { feeForSize: trimmed, isPerSize: false, rawString: trimmed };
  }

  // Normalize size labels for comparison: strip "$" prefix, uppercase
  const normalizeSize = (s) => s.replace(/^\$/, "").trim().toUpperCase();

  let feeForSize = null;
  if (sizeLabel) {
    const target = normalizeSize(sizeLabel);
    const hit = parsed.find((p) => normalizeSize(p.size) === target);
    feeForSize = hit ? hit.fee : null;
  }

  return { feeForSize, isPerSize: true, rawString: trimmed };
};


/**
 * Parse reset fee string and resolve per-size fee when possible.
 *
 * Patterns handled:
 *  1  Flat fee:       "$50", "$47", "$40"
 *  2  Slash-separated (positional, maps to firm sizes): "$29/$39/$79/$119"
 *  3  Range:          "$59-$199 (varies by size)", "$80-$150"
 *  4  Free-or-paid:   "Free on rebill / $78 manual", "Free on rebill / $250+ (Buffer/BluLive)"
 *  5  Vague:          "Varies by size", "Contact support"
 *  6  None/null:      null, "", "None", "N/A"
 *
 * @param {string|null|undefined} rawString — reset_fee from firm object
 * @param {string} [sizeLabel] — e.g. "$50K", "100K"
 * @param {string[]} [firmSizes] — ordered array of all firm size labels (for positional mapping)
 * @returns {{ fee: number|null, confidence: "exact"|"estimated"|"unknown", display: string|null, note: string|null }}
 */
export const parseResetFee = (rawString, sizeLabel, firmSizes = []) => {
  // Pattern 6 — null / empty / none
  if (!rawString || rawString.trim() === "") {
    return { fee: null, confidence: "unknown", display: null, note: null };
  }

  const trimmed = rawString.trim();
  const lower = trimmed.toLowerCase();

  if (lower === "none" || lower === "n/a" || lower === "$0" || lower === "0") {
    return { fee: 0, confidence: "exact", display: "$0", note: "No reset fee" };
  }

  // Helper: extract first dollar amount from a string
  const extractDollar = (s) => {
    const m = s.match(/\$(\d[\d,.]*)/);
    return m ? parseFloat(m[1].replace(/,/g, "")) : null;
  };

  // Pattern 4 — "Free on rebill / $X manual" or similar free-or-paid
  if (lower.includes("free") && /\$\d/.test(trimmed)) {
    const paidPart = trimmed.split("/").find(s => /\$\d/.test(s)) || trimmed;
    const val = extractDollar(paidPart);
    if (val != null) {
      return { fee: val, confidence: "estimated", display: `$${val}`, note: "Manual reset (free on rebill)" };
    }
  }

  // Pattern 1 — Flat fee: just "$XX" with no slashes, no ranges
  if (/^\$\d[\d,.]*$/.test(trimmed)) {
    const val = extractDollar(trimmed);
    return { fee: val, confidence: "exact", display: `$${val}`, note: null };
  }

  // Pattern 2 — Slash-separated positional: "$29/$39/$79/$119"
  if (/^\$\d[\d,.]*(\s*\/\s*\$\d[\d,.]*)+$/.test(trimmed)) {
    const parts = trimmed.split(/\s*\/\s*/).map(s => extractDollar(s)).filter(v => v != null);
    // Try positional mapping to firm sizes
    if (sizeLabel && firmSizes.length === parts.length) {
      const normalizeSize = (s) => s.replace(/^\$/, "").trim().toUpperCase();
      const target = normalizeSize(sizeLabel);
      const idx = firmSizes.findIndex(s => normalizeSize(s) === target);
      if (idx >= 0 && idx < parts.length) {
        return { fee: parts[idx], confidence: "exact", display: `$${parts[idx]}`, note: `For ${sizeLabel} account` };
      }
    }
    // Fallback: midpoint of range
    const min = Math.min(...parts);
    const max = Math.max(...parts);
    const mid = Math.round((min + max) / 2);
    return { fee: mid, confidence: "estimated", display: `~$${mid}`, note: `Midpoint of $${min}–$${max} range` };
  }

  // Pattern 3 — Range: "$59-$199" or "$80-$150"
  const rangeMatch = trimmed.match(/\$(\d[\d,.]*)\s*[-–]\s*\$(\d[\d,.]*)/);
  if (rangeMatch) {
    const lo = parseFloat(rangeMatch[1].replace(/,/g, ""));
    const hi = parseFloat(rangeMatch[2].replace(/,/g, ""));
    const mid = Math.round((lo + hi) / 2);
    return { fee: mid, confidence: "estimated", display: `~$${mid}`, note: `Midpoint of $${lo}–$${hi} range` };
  }

  // Pattern 5 — Vague text ("Varies by size", "Contact support")
  // Last resort: try to extract any dollar amount
  const anyDollar = extractDollar(trimmed);
  if (anyDollar != null) {
    return { fee: anyDollar, confidence: "estimated", display: `~$${anyDollar}`, note: `Estimated from "${trimmed}"` };
  }

  return { fee: null, confidence: "unknown", display: null, note: `Cannot parse: "${trimmed}"` };
};
